import { useState } from 'react';
import {
  Stack,
  Text,
  Button,
  Select,
  HStack,
  VStack,
  Box,
  IconButton,
  RadioGroup,
  createListCollection,
} from '@chakra-ui/react';
import { MdAdd, MdClose, MdDelete } from 'react-icons/md';
import { polygonAttributes } from '@/lib/constants/polygon-attributes';
import { useQuery } from '@/hooks/use-query';
import { QueryCondition } from '@/lib/types';

function Condition({    
  condition, 
  index, 
  removeCondition, 
  updateCondition, 
}: {
  condition: QueryCondition,
  index: number,
  removeCondition: (id: string) => void,
  updateCondition: (id: string, updates: Partial<QueryCondition>) => void,
}) {
  const selectedAttribute = condition.attribute || '';
  const selectedValues: string[] = Array.isArray(condition.value)
    ? condition.value
    : condition.value
      ? [condition.value]
      : [];
  const [error, setError] = useState<string | null>(null);

  const attributeCollection = createListCollection({
    items: polygonAttributes.map((attr) => ({
      label: attr.name,
      value: attr.databaseID, // Use databaseID for CQL compatibility
    })),
  });

  const selectedAttributeData = polygonAttributes.find((attr) => attr.databaseID === selectedAttribute);
  const valueCollection = createListCollection({
    items:
      selectedAttributeData?.values.map((val) => ({
        label: val.name,
        value: val.value,
      })) || [],
  });

  const handleAttributeChange = (details: any) => {
    if (details?.value && details.value.length > 0) {
      const newAttribute = details.value[0];
      setError(null);
      
      // Check if current values are valid for the new attribute
      const newAttributeData = polygonAttributes.find((attr) => attr.databaseID === newAttribute);
      const validValues = selectedValues.filter(value => 
        newAttributeData?.values.some(val => val.value === value)
      );
      
      updateCondition(condition.id, {
        attribute: newAttribute,
        value: validValues.length > 0 ? validValues : '', // Only keep valid values for new attribute
      });
    }
  };

  const handleValueChange = (details: any) => {
    if (details?.value) {
      const newValues = details.value;
      setError(null);
      updateCondition(condition.id, {
        value: newValues,
      });
    }
  };

  const handleLogicalOperatorChange = (operator: 'AND' | 'OR') => {
    updateCondition(condition.id, {
      logicalOperator: operator,
    });
  };

  const handleClearFilter = () => {
    removeCondition(condition.id)
  };

  return (
    <VStack 
      gap='3' 
      align='stretch' 
      width='full' 
      p='3' 
      borderRadius='md' 
      border='1px solid' 
      borderColor={index === 0 ? 'gray.300' : 'gray.200'}
      bg={index === 0 ? 'gray.50' : undefined}
      boxShadow={index === 0 ? 'xs' : undefined}
    >
      <HStack justify='space-between' align='start'>
        {index > 0 ? (
          <VStack align='start' gap='1' flex='1'>
            <Text fontSize='xs' color='gray.500' fontWeight='medium'>
              Logical Operator:
            </Text>
            <RadioGroup.Root
              size='xs'
              value={condition.logicalOperator || 'AND'}
              onValueChange={(e) => {
                const val = e.value;
                if (val === 'AND' || val === 'OR') {
                  handleLogicalOperatorChange(val);
                }
              }}
            >
              <HStack gap='3'>
                <RadioGroup.Item value='AND'>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>AND</RadioGroup.ItemText>
                </RadioGroup.Item>
                <RadioGroup.Item value='OR'>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>OR</RadioGroup.ItemText>
                </RadioGroup.Item>
              </HStack>
            </RadioGroup.Root>
          </VStack>
        ) : (
          <Box flex='1'>
            <Text fontSize='xs' color='gray.600' fontWeight='medium'>
              Condition
            </Text>
          </Box>
        )}
        <IconButton
          size='sm'
          variant='ghost'
          aria-label='Remove condition'
          onClick={handleClearFilter}
        >
          <MdClose />
        </IconButton>
      </HStack>
      
      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}
      
      <VStack gap='2' align='stretch'>
        <Select.Root
          size='sm'
          collection={attributeCollection}
          value={selectedAttribute ? [selectedAttribute] : []}
          onValueChange={handleAttributeChange}
        >
          <Select.Label color='gray.500'>Filter polygons where:</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder='Select attribute' />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content bg='white' maxH='200px' overflowY='auto'>
              {attributeCollection.items.map((attr) => (
                <Select.Item item={attr} key={attr.value}>
                  {attr.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>

        <Text fontSize='sm' color='gray.500' fontWeight='medium'>is</Text>
        {selectedValues.length > 1 && (
          <Text fontSize='xs' color='blue.500' fontWeight='medium'>
            {selectedValues.length} values selected (OR condition)
          </Text>
        )}
        <Select.Root
          size='sm'
          collection={valueCollection}
          value={selectedValues}
          onValueChange={handleValueChange}
          disabled={!selectedAttribute}
          multiple
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder='Select one or more values' />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content bg='white' maxH='200px' overflowY='auto'>
              {valueCollection.items.map((val) => (
                <Select.Item item={val} key={val.value}>
                  {val.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </VStack>

      <HStack />
  </VStack>
  );
}

export default function Query() {
  const { queryState, addCondition, removeCondition, updateCondition, clearFilter } = useQuery();

  return (
    <Stack 
      gap='4' 
      align='flex-start' 
      width='full'
      maxH='calc(100vh - 200px)'
      overflowY='auto'
      css={{
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}
    >
      <VStack gap='2' align='flex-start' width='full'>
        <Text fontSize='lg' fontWeight='semibold'>
          Filter Polygons
        </Text>
      </VStack>

      {queryState && queryState.length > 0 && (
        <Box width='full'>
          <Text fontSize='sm' color='gray.600' mb='2'>
            Show polygons where:
          </Text>
          <VStack gap='2' align='stretch' width='full' pr='2'>
            {queryState.map((condition: any, index: number) => (
              <Condition 
                key={condition.id} 
                condition={condition} 
                index={index}
                removeCondition={removeCondition}
                updateCondition={updateCondition}
              />
            ))}
          </VStack>
        </Box>
      )}

      <HStack gap='2'>
        <Button
          size='sm'
          colorScheme='blue'
          variant='outline'
          onClick={addCondition}
        >
          <MdAdd />
          Add Condition
        </Button>
        {queryState && queryState.length > 0 && (
          <Button
            size='sm'
            variant='outline'
            onClick={clearFilter}
          >
            <MdDelete />
            Clear
          </Button>
        )}
      </HStack>
    </Stack>
  );
}
